import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShoppingCart, Gift, ChevronRight, X, Plus, Minus, CheckCircle, CreditCard } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatAmount } from '../lib/currencyUtils';
import api from '../api/client';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function ShopPage() {
  const { user } = useAuth();
  const { symbol, currency: activeCurrency } = useCurrency();
  const navigate = useNavigate();
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paying, setPaying] = useState(false);
  const [paySuccess, setPaySuccess] = useState(null);
  const [isGift, setIsGift] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [giftMessage, setGiftMessage] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/shop/voucher-types').catch(() => ({ data: [] })),
      api.get('/api/config/admin/settings/public-site').catch(() => ({ data: {} })),
    ]).then(([vRes, sRes]) => {
      setVoucherTypes(vRes.data);
      setSite(sRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const openPurchase = (type) => {
    if (!user) { navigate('/account'); return; }
    setSelectedType(type);
    setSelectedAmount((type.allowed_amounts || [])[0]);
    setQuantity(1);
    setPhoneNumber('');
    setPaySuccess(null);
    setIsGift(false);
    setRecipientEmail('');
    setGiftMessage('');
  };

  const handlePay = async () => {
    if (!phoneNumber || phoneNumber.length < 9) {
      toast.error('Enter a valid M-Pesa phone number');
      return;
    }
    if (isGift && (!recipientEmail || !recipientEmail.includes('@'))) {
      toast.error('Enter a valid recipient email');
      return;
    }
    setPaying(true);
    try {
      const orderPayload = {
        items: [{ voucher_type_id: selectedType.id, amount: selectedAmount, quantity }],
        payment_method: 'mpesa',
        mpesa_phone: phoneNumber,
      };
      if (isGift && recipientEmail) {
        orderPayload.recipient_email = recipientEmail;
        orderPayload.gift_message = giftMessage;
      }
      const orderRes = await api.post('/api/shop/customer/orders', orderPayload);
      const payRes = await api.post(`/api/shop/customer/orders/${orderRes.data.id}/pay`, { mpesa_phone: phoneNumber });
      setPaySuccess(payRes.data);
      toast.success(isGift ? 'Gift voucher sent!' : 'Payment successful!');
    } catch (err) {
      toast.error(err.displayMessage || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const heroImage = site?.hero_image_url
    ? (site.hero_image_url.startsWith('http') ? site.hero_image_url : `${API_BASE}${site.hero_image_url}`)
    : '';
  const promoImage = site?.promo_banner_image_url
    ? (site.promo_banner_image_url.startsWith('http') ? site.promo_banner_image_url : `${API_BASE}${site.promo_banner_image_url}`)
    : '';

  return (
    <div className="min-h-screen bg-[#FFFCF9]" style={{ fontFamily: 'Roboto, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#561D1A]/90 to-[#561D1A]/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-2xl animate-fade-in">
            {site?.hero_badge_text && (
              <span className="inline-block bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-medium px-3 py-1 rounded-full mb-4 tracking-wider uppercase">
                {site.hero_badge_text}
              </span>
            )}
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white font-bold leading-tight mb-6" data-testid="hero-title">
              {site?.hero_title || 'Give the Gift of Choice'}
            </h1>
            <p className="text-white/80 text-lg mb-8 leading-relaxed" data-testid="hero-subtitle">
              {site?.hero_subtitle || 'Shop for premium gift vouchers redeemable at leading stores across the mall.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#shop" className="bg-[#EC1A23] hover:bg-[#C4121A] text-white px-8 py-3 rounded-full font-medium transition-all hover:shadow-lg hover:scale-105 active:scale-95 flex items-center gap-2" data-testid="hero-shop-btn">
                <ShoppingCart size={18} /> Browse Vouchers
              </a>
              {!user && (
                <button onClick={() => navigate('/account')} className="border-2 border-white/60 text-white hover:bg-white/10 px-8 py-3 rounded-full font-medium transition-all flex items-center gap-2">
                  Sign In <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="bg-[#561D1A] text-white py-4">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center gap-8 text-sm">
          {[
            [site?.stats_stores || '100+', 'Partner Stores'],
            [site?.stats_vouchers || '50K+', 'Vouchers Issued'],
            [(site?.stats_rating || '4.9') + '\u2605', 'Customer Rating'],
            [site?.stats_delivery || 'Instant', 'Digital Delivery']
          ].map(([val, label]) => (
            <div key={label} className="text-center">
              <div className="font-heading text-xl font-bold text-[#D4AF37]">{val}</div>
              <div className="text-white/60 text-xs">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Voucher Types Grid */}
      <section id="shop" className="max-w-7xl mx-auto px-6 py-16" data-testid="shop-section">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl font-bold text-[#561D1A] mb-3">Browse Vouchers</h2>
          <p className="text-gray-500 max-w-lg mx-auto">Choose from our curated selection of gift vouchers for any occasion</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />)}
          </div>
        ) : voucherTypes.length === 0 ? (
          <div className="text-center py-20">
            <Gift size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No vouchers available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {voucherTypes.map((type, idx) => {
              const artworkSrc = type.artwork_url
                ? (type.artwork_url.startsWith('http') ? type.artwork_url : `${API_BASE}${type.artwork_url}`)
                : type.image_url
                  ? (type.image_url.startsWith('http') ? type.image_url : `${API_BASE}${type.image_url}`)
                  : null;
              return (
                <div key={type.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_-8px_rgba(86,29,26,0.12)] border border-[#D4AF37]/15 hover:shadow-[0_20px_40px_-10px_rgba(86,29,26,0.2)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={() => openPurchase(type)}
                  data-testid="voucher-type-card">
                  {artworkSrc ? (
                    <img src={artworkSrc} alt={type.name} className="w-full h-48 object-cover" />
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-[#561D1A] to-[#3E1412] flex items-center justify-center">
                      <Gift size={48} className="text-[#D4AF37]/60" strokeWidth={1} />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-bold text-[#561D1A] mb-2">{type.name}</h3>
                    <p className="text-gray-500 text-sm mb-4 leading-relaxed line-clamp-2">{type.description}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-400 mb-1">From</p>
                        <p className="font-heading text-2xl font-bold text-[#EC1A23]">
                          {type.allowed_amounts?.length ? formatAmount(Math.min(...type.allowed_amounts), activeCurrency) : '—'}
                        </p>
                      </div>
                      <button className="bg-[#EC1A23] hover:bg-[#C4121A] text-white px-5 py-2 rounded-full text-sm font-medium transition-all hover:shadow-lg group-hover:scale-105" data-testid="buy-voucher-btn">
                        Buy Now
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(type.allowed_amounts || []).map(amt => (
                        <span key={amt} className="text-xs bg-[#561D1A]/5 text-[#561D1A] px-2 py-0.5 rounded-full border border-[#561D1A]/10">
                          {symbol} {amt.toLocaleString()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Promo Banner */}
      {promoImage && (
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <div className="relative rounded-2xl overflow-hidden h-72" data-testid="promo-banner">
            <img src={promoImage} alt="Promo" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent flex items-center">
              <div className="px-10">
                <h2 className="font-heading text-4xl font-bold text-white mb-2">{site?.promo_banner_title || 'Special Offer'}</h2>
                <p className="text-white/80 mb-4">{site?.promo_banner_subtitle || ''}</p>
                <a href="#shop" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#561D1A] px-6 py-2.5 rounded-full text-sm font-bold hover:bg-[#C4A030] transition-all">
                  Shop Now <ChevronRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WExclusive Membership CTA */}
      <section className="bg-gradient-to-r from-[#2A2A2A] to-[#4A4A4A] py-16">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center gap-10">
          <div className="flex-shrink-0">
            <img src={`${API_BASE}/api/uploads/platinum_card.png`} alt="Platinum Card" className="w-64 rounded-xl shadow-2xl" data-testid="platinum-card-promo" />
          </div>
          <div className="text-white">
            <span className="inline-block bg-[#D4AF37]/30 text-[#D4AF37] text-xs font-bold px-3 py-1 rounded-full mb-3 uppercase tracking-wider">WExclusive</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-3">Platinum VIP Card</h2>
            <p className="text-white/70 mb-6 max-w-lg leading-relaxed">
              Join WExclusive with a 1-year membership and receive your Platinum Card. Enjoy exclusive discounts at all participating stores, earn 2x loyalty points, and get VIP event invitations.
            </p>
            <button onClick={() => navigate(user ? '/profile' : '/account')}
              className="bg-[#D4AF37] hover:bg-[#C4A030] text-[#2A2A2A] px-8 py-3 rounded-full font-bold text-sm transition-all hover:shadow-lg flex items-center gap-2">
              <CreditCard size={16} /> {user ? 'Get Your Card' : 'Sign Up Now'}
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#561D1A]/5 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-heading text-3xl font-bold text-[#561D1A] text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Choose & Buy', desc: 'Select your voucher type and amount. Pay securely via M-Pesa.' },
              { step: '02', title: 'Get Instantly', desc: 'Your digital voucher with unique QR code is added to your profile immediately.' },
              { step: '03', title: 'Redeem Anywhere', desc: 'Show your QR code at any participating store in the mall.' },
            ].map(item => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 bg-[#EC1A23] text-white rounded-2xl flex items-center justify-center text-xl font-heading font-bold mx-auto mb-4 shadow-lg">{item.step}</div>
                <h3 className="font-heading text-xl font-bold text-[#561D1A] mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Purchase Modal */}
      {selectedType && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="purchase-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="h-2 bg-gradient-to-r from-[#561D1A] via-[#EC1A23] to-[#D4AF37] rounded-t-2xl" />
            <div className="p-6">
              {paySuccess ? (
                <div className="text-center py-4">
                  <CheckCircle size={56} className="text-emerald-500 mx-auto mb-4" />
                  <h3 className="font-heading text-2xl font-bold text-[#561D1A] mb-2">Payment Successful!</h3>
                  <p className="text-gray-500 text-sm mb-2">
                    {paySuccess.vouchers_created} voucher{paySuccess.vouchers_created > 1 ? 's' : ''} added to your profile.
                  </p>
                  <p className="text-xs text-gray-400 font-mono">Ref: {paySuccess.transaction_id}</p>
                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setSelectedType(null)} className="flex-1 border-2 border-[#561D1A] text-[#561D1A] py-2.5 rounded-full text-sm font-medium">Continue Shopping</button>
                    <button onClick={() => { setSelectedType(null); navigate('/profile'); }} className="flex-1 bg-[#EC1A23] text-white py-2.5 rounded-full text-sm font-medium">View Vouchers</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <h3 className="font-heading text-xl font-bold text-[#561D1A]">{selectedType.name}</h3>
                      <p className="text-gray-400 text-sm mt-0.5">Select amount & pay</p>
                    </div>
                    <button onClick={() => setSelectedType(null)} className="text-gray-400 hover:text-gray-600 p-1"><X size={20} /></button>
                  </div>
                  <div className="mb-5">
                    <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Select Amount</label>
                    <div className="grid grid-cols-2 gap-2">
                      {(selectedType.allowed_amounts || []).map(amt => (
                        <button key={amt} onClick={() => setSelectedAmount(amt)}
                          className={`py-3 px-2 rounded-xl text-sm font-medium border-2 transition-all ${selectedAmount === amt ? 'bg-[#EC1A23] border-[#EC1A23] text-white shadow-md' : 'border-gray-200 text-gray-600 hover:border-[#EC1A23]/40'}`}
                          data-testid="amount-option">
                          {formatAmount(amt, activeCurrency)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Quantity</label>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#EC1A23]"><Minus size={14} /></button>
                      <span className="font-heading text-xl font-bold text-[#561D1A] w-8 text-center" data-testid="quantity-display">{quantity}</span>
                      <button onClick={() => setQuantity(Math.min(10, quantity + 1))} className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#EC1A23]"><Plus size={14} /></button>
                    </div>
                  </div>
                  <div className="bg-[#561D1A]/5 rounded-xl p-4 mb-5">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Total</span>
                      <span className="font-heading text-2xl font-bold text-[#561D1A]" data-testid="order-total">{formatAmount((selectedAmount || 0) * quantity, activeCurrency)}</span>
                    </div>
                  </div>
                  <div className="mb-5">
                    <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">M-Pesa Phone Number</label>
                    <input type="tel" placeholder="e.g. 0712345678" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)}
                      className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none transition-colors bg-transparent" data-testid="mpesa-phone-input" />
                  </div>

                  {/* Gift toggle */}
                  <div className="mb-5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={isGift} onChange={e => setIsGift(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#EC1A23] focus:ring-[#EC1A23]" data-testid="gift-toggle" />
                      <span className="text-sm text-gray-700 flex items-center gap-1.5">
                        <Gift size={14} className="text-[#D4AF37]" /> Send as a gift to someone else
                      </span>
                    </label>
                    {isGift && (
                      <div className="mt-3 space-y-3 pl-6 border-l-2 border-[#D4AF37]/30">
                        <div>
                          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Recipient Email *</label>
                          <input type="email" placeholder="friend@example.com" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)}
                            className="w-full border-b-2 border-gray-200 focus:border-[#D4AF37] py-2 text-sm outline-none bg-transparent" data-testid="recipient-email-input" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Gift Message (optional)</label>
                          <input type="text" placeholder="Happy Birthday!" value={giftMessage} onChange={e => setGiftMessage(e.target.value)}
                            className="w-full border-b-2 border-gray-200 focus:border-[#D4AF37] py-2 text-sm outline-none bg-transparent" data-testid="gift-message-input" />
                        </div>
                      </div>
                    )}
                  </div>

                  <button onClick={handlePay} disabled={paying}
                    className="w-full bg-[#EC1A23] hover:bg-[#C4121A] disabled:opacity-60 text-white py-3 rounded-full font-medium text-sm transition-all hover:shadow-lg flex items-center justify-center gap-2" data-testid="pay-btn">
                    {paying ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>) : (`Pay ${formatAmount((selectedAmount || 0) * quantity, activeCurrency)} via M-Pesa`)}
                  </button>
                  <p className="text-center text-xs text-gray-400 mt-3">Mock M-Pesa - No real payment</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
